import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { EmptyView } from '@euler/components/EmptyView';
import { TypewriterText } from '@euler/components/typography/TypewriterText';
import { useTypewriter } from '@euler/components/typography/useTypewriter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import {
  CoachingCaseCard,
  CoachingLibraryCatalogue,
  CoachingSuggestion,
} from '@euler/model/coaching';
import { CoachingStreakView } from '@euler/model/coaching-progress';
import { MessagesSquare } from 'lucide-react-native';
import { memo, useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { CaseCard } from '../../components/CaseCard';
import { CaseCardSkeleton } from '../../components/CaseCardSkeleton';
import { CoachingPrompt } from '../../components/CoachingPrompt';
import { CollectionStrip } from '../../components/CollectionStrip';
import { SuggestionReason } from '../../components/SuggestionReason';
import { StreakCard } from '../../progress/components/StreakCard';

type Collection = CoachingLibraryCatalogue['collections'][number];

/** 销售对练's start page: a greeting, the prompt, suggestions, collections. */
export const CoachingHomeView = memo(
  ({
    name,
    streak,
    suggestions,
    catalogue,
    failed,
    onRetry,
    onDescribe,
    onPractise,
    onBrowse,
    onOpenCollection,
  }: {
    name: string | null;
    /** Null while loading, or when it did not load: then it is left out. */
    streak: CoachingStreakView | null;
    suggestions: CoachingSuggestion[] | null;
    catalogue: CoachingLibraryCatalogue | null;
    failed: boolean;
    onRetry: () => void;
    onDescribe: (text: string) => void;
    onPractise: (card: CoachingCaseCard) => void;
    onBrowse?: () => void;
    onOpenCollection?: (collection: Collection) => void;
  }) => {
    const styles = useStyles();
    const { rpx } = useResponsiveContext();
    const wide = useIsDesktopLayout();
    const firstVisit = suggestions != null && !name;
    const title = name ? `${name}，今天想练哪种对话？` : '今天想练哪种对话？';
    const subtitle = firstVisit
      ? '第一次来？从一个基础场景开始吧。'
      : '选一个推荐的场景，或者直接说说你遇到的情况。';
    const [titleTyped, subtitleTyped] = useTypewriter([title, subtitle]);
    const total = catalogue?.collections.reduce(
      (sum, item) => sum + item.caseCount,
      0,
    );
    return (
      <ScrollView contentContainerStyle={styles.page}>
        <View style={styles.greeting}>
          <View style={styles.badge}>
            <MessagesSquare size={rpx(22)} color={aiColors.accentForeground} />
          </View>
          <View style={styles.flex}>
            <TypewriterText
              text={title}
              typed={titleTyped}
              size={rpx(20)}
              bold
              color={aiColors.foreground}
            />
            <TypewriterText
              text={subtitle}
              typed={subtitleTyped}
              size={rpx(13)}
              color={aiColors.mutedForeground}
              style={styles.subtitle}
            />
          </View>
        </View>

        <View style={styles.section}>
          <CoachingPrompt onSubmit={onDescribe} />
        </View>

        {streak ? (
          <View style={styles.section}>
            <StreakCard streak={streak} />
          </View>
        ) : null}

        <View style={styles.section}>
          <View style={styles.sectionHead}>
            <Typography.Text size={rpx(16)} bold color={aiColors.foreground}>
              推荐场景
            </Typography.Text>
            {onBrowse ? (
              <Pressable onPress={onBrowse} accessibilityRole="link">
                <Typography.Text size={rpx(12)} color={aiColors.primary}>
                  查看全部案例 →
                </Typography.Text>
              </Pressable>
            ) : null}
          </View>
          {failed ? (
            <EmptyView
              message="推荐暂时没加载出来"
              onRetry={onRetry}
              noFullHeight
            />
          ) : suggestions == null ? (
            <View style={[styles.grid, wide && styles.gridWide]}>
              {[0, 1, 2].map(index => (
                <View key={index} style={wide ? styles.cellWide : styles.cell}>
                  <CaseCardSkeleton />
                </View>
              ))}
            </View>
          ) : (
            <View style={[styles.grid, wide && styles.gridWide]}>
              {suggestions.map(item => (
                <View
                  key={item.case.id}
                  style={wide ? styles.cellWide : styles.cell}
                >
                  <CaseCard
                    card={item.case}
                    level={item.difficulty}
                    onPress={() => onPractise(item.case)}
                    footer={<SuggestionReason reason={item.reason} />}
                  />
                </View>
              ))}
            </View>
          )}
        </View>

        {catalogue?.collections.length && onOpenCollection ? (
          <View style={styles.section}>
            <View style={styles.sectionHead}>
              <Typography.Text size={rpx(16)} bold color={aiColors.foreground}>
                系列合集
              </Typography.Text>
              <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
                {`共 ${total} 个场景`}
              </Typography.Text>
            </View>
            <View style={styles.strip}>
              <CollectionStrip
                collections={catalogue.collections}
                onOpen={onOpenCollection}
              />
            </View>
          </View>
        ) : null}
      </ScrollView>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        page: {
          width: '100%',
          maxWidth: rpx(768),
          alignSelf: 'center',
          paddingHorizontal: rpx(16),
          paddingTop: rpx(24),
          paddingBottom: rpx(32),
        },
        greeting: { flexDirection: 'row', gap: rpx(12) },
        badge: {
          width: rpx(44),
          height: rpx(44),
          borderRadius: rpx(16),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint(aiColors.green, 0.14),
        },
        flex: { flex: 1 },
        subtitle: { marginTop: rpx(4) },
        section: { marginTop: rpx(24) },
        sectionHead: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        grid: { marginTop: rpx(12), gap: rpx(10) },
        gridWide: { flexDirection: 'row', flexWrap: 'wrap' },
        cell: { width: '100%' },
        cellWide: { width: '49%' },
        strip: { marginTop: rpx(12) },
      }),
    [rpx],
  );
}
